/** "Đánh giá chất lượng ảnh": the owner rates a finished photo (1-5 stars + optional comment), Turnstile-checked on
 * the server; sending again changes the rating. The operator reads them in /admin. */
import { useEffect, useId, useState } from "react";
import { ApiError, type Api, type Feedback, type Job } from "../api";
import { RATING_LABELS, errorMessage } from "../messages";

export const COMMENT_MAX = 1000;

interface Props {
  api: Api;
  job: Job;
  onRated: (feedback: Feedback) => void;
}

export function RateResult({ api, job, onRated }: Props) {
  const [rating, setRating] = useState(job.feedback?.rating ?? 0);
  const [comment, setComment] = useState(job.feedback?.comment ?? "");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = useId();
  const commentId = useId();

  useEffect(() => {                                   // another photo is shown
    setRating(job.feedback?.rating ?? 0);
    setComment(job.feedback?.comment ?? "");
    setSent(false);
    setError(null);
  }, [job.id]);

  async function send() {
    setSending(true);
    setError(null);
    try {
      const fb = await api.rateJob(job.id, rating, comment.trim());
      setSent(true);
      onRated(fb);
    } catch (e) {
      setError(errorMessage(e instanceof ApiError ? e.code : "network"));
    } finally {
      setSending(false);
    }
  }

  return (
    <fieldset className="rate">
      <legend>Đánh giá chất lượng ảnh</legend>
      <div className="stars" role="radiogroup" aria-label="Số sao">
        {[1, 2, 3, 4, 5].map((n) => (
          <label key={n} className={`star${n <= rating ? " is-on" : ""}`} title={RATING_LABELS[n]}>
            <input type="radio" name={name} value={n} checked={rating === n} aria-label={`${n} sao: ${RATING_LABELS[n]}`}
                   onChange={() => { setRating(n); setSent(false); }} />
            <span aria-hidden="true">★</span>
          </label>
        ))}
        <span className="star-label muted">{rating ? RATING_LABELS[rating] : "Chọn số sao"}</span>
      </div>
      <label htmlFor={commentId} className="visually-hidden">Góp ý thêm</label>
      <textarea id={commentId} className="text-input" rows={2} maxLength={COMMENT_MAX} value={comment}
                placeholder="Góp ý thêm về ảnh (không bắt buộc)" onChange={(e) => { setComment(e.target.value); setSent(false); }} />
      <div className="actions">
        <button type="button" className="btn btn-small" disabled={!rating || sending} onClick={() => void send()}>
          {sending ? "Đang gửi…" : job.feedback ? "Cập nhật đánh giá" : "Gửi đánh giá"}
        </button>
        {sent && <span role="status" className="ok">Cảm ơn bạn đã đánh giá!</span>}
      </div>
      {error && <p role="alert" className="error">{error}</p>}
    </fieldset>
  );
}
